import {
  Trophy, CalendarHeart, TrendingUp, Utensils, Clock, Flame, TrendingDown, Medal, Heart, Users,
} from "lucide-react";

// ─── Texty zaujímavostí ───────────────────────────────────────────────────────
// Backend (GET /api/manager/overview-insights) posiela len čísla a typ,
// slová skladá až frontend. Neznámy typ alebo neúplná zaujímavosť sa
// zahodí (null) -- nový typ na backende tak starší frontend nezhodí.

const DNI = {
  1: { nazov: "Pondelok", v: "v pondelok", z: "pondelkov" },
  2: { nazov: "Utorok",   v: "v utorok",   z: "utorkov" },
  3: { nazov: "Streda",   v: "v stredu",   z: "stried" },
  4: { nazov: "Štvrtok",  v: "vo štvrtok", z: "štvrtkov" },
  5: { nazov: "Piatok",   v: "v piatok",   z: "piatkov" },
  6: { nazov: "Sobota",   v: "v sobotu",   z: "sobôt" },
  7: { nazov: "Nedeľa",   v: "v nedeľu",   z: "nedieľ" },
};

const NBSP = " ";

const jeCislo = (v) => typeof v === "number" && Number.isFinite(v);
const jeText = (v) => typeof v === "string" && v.trim() !== "";
const velkym = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const formatEur = (n) =>
  `${Number(n).toLocaleString("sk-SK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${NBSP}€`;

export const formatPercento = (podiel) => `${Math.round(Math.abs(podiel) * 100)}${NBSP}%`;

export const formatKusy = (n) =>
  Number(n).toLocaleString("sk-SK", { maximumFractionDigits: 1 });

/** Slovenský tvar podľa počtu: [1, 2–4, 0 a 5+] -- "1 objednávka", "3 objednávky", "12 objednávok". */
export function sklon(n, [jeden, dvaAzStyri, viac]) {
  if (n === 1) return jeden;
  if (Number.isInteger(n) && n >= 2 && n <= 4) return dvaAzStyri;
  return viac;
}

const OBJEDNAVKY = ["objednávka", "objednávky", "objednávok"];
const ZAKAZNICI = ["zákazník", "zákazníci", "zákazníkov"];

/** '09:05' -> '9:05' */
const formatCas = (hhmm) => hhmm.replace(/^0(\d)/, "$1");

/** 'YYYY-MM-DD' -> '23. 6. 2026' (bez Date -- žiadny posun časovým pásmom). */
export function formatDatum(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return null;
  return `${Number(m[3])}. ${Number(m[2])}. ${m[1]}`;
}

/** " – o 30 % viac ako …" / " – o 12 % menej ako …"; nič pri null. */
const rozdielText = (rozdiel, coho) => {
  if (!jeCislo(rozdiel) || Math.round(Math.abs(rozdiel) * 100) === 0) return "";
  return ` – o ${formatPercento(rozdiel)} ${rozdiel > 0 ? "viac" : "menej"} ako ${coho}`;
};

const FORMATY = {
  TOP_JEDLO: (i) => {
    if (!jeText(i.nazov) || !jeCislo(i.kusov)) return null;
    return {
      Icon: Trophy,
      ton: "primary",
      nadpis: "Najpredávanejšie jedlo",
      hodnota: i.nazov,
      popis: `Predalo sa ${i.kusov}×`
        + (jeCislo(i.podiel) ? ` – to je ${formatPercento(i.podiel)} hlavných jedál z denného menu.` : "."),
    };
  },

  DEN_A_JEDLO: (i) => {
    const den = DNI[i.denVTyzdni];
    if (!den || !jeText(i.nazov) || !jeCislo(i.priemerTrzba)) return null;
    return {
      Icon: CalendarHeart,
      ton: "primary",
      nadpis: "Najvýnosnejšie jedlo a deň",
      hodnota: `${i.nazov} ${den.v}`,
      popis: `${velkym(den.v)} prinesie v priemere ${formatEur(i.priemerTrzba)}`
        + (jeCislo(i.priemerKs) ? ` (${formatKusy(i.priemerKs)}${NBSP}ks)` : "")
        + rozdielText(i.rozdiel, "v iné dni")
        + ".",
    };
  },

  NAJSILNEJSI_DEN: (i) => {
    const den = DNI[i.denVTyzdni];
    if (!den || !jeCislo(i.priemerTrzba)) return null;
    return {
      Icon: TrendingUp,
      ton: "success",
      nadpis: "Najsilnejší deň v týždni",
      hodnota: den.nazov,
      popis: `Priemerná tržba ${formatEur(i.priemerTrzba)} za deň`
        + rozdielText(i.rozdiel, "ostatné dni")
        + "."
        + (jeCislo(i.dni) ? ` Počítané z ${i.dni} ${den.z}.` : ""),
    };
  },

  REKORDNY_DEN: (i) => {
    const datum = formatDatum(i.den);
    if (!datum || !jeCislo(i.trzba) || !jeCislo(i.objednavok)) return null;
    const den = DNI[i.denVTyzdni];
    return {
      Icon: Medal,
      ton: "success",
      nadpis: "Rekordný deň",
      hodnota: den ? `${datum} (${den.nazov.toLowerCase()})` : datum,
      // Po "z" ide genitív: "z 1 objednávky", "z 3 objednávok".
      popis: `Tržba ${formatEur(i.trzba)} z ${i.objednavok} ${i.objednavok === 1 ? "objednávky" : "objednávok"}`
        // Násobok pod 1,1 nie je nič, čím by sa oplatilo chváliť.
        + (jeCislo(i.nasobokPriemeru) && i.nasobokPriemeru >= 1.1
          ? ` – ${formatKusy(i.nasobokPriemeru)}-násobok priemerného dňa.`
          : "."),
    };
  },

  NAJVERNEJSI_ZAKAZNIK: (i) => {
    if (!jeText(i.meno) || !jeCislo(i.objednavok)) return null;
    const od = formatDatum(i.od);
    return {
      Icon: Heart,
      ton: "primary",
      nadpis: "Najvernejší zákazník",
      hodnota: i.meno.trim(),
      popis: `${i.objednavok} ${sklon(i.objednavok, OBJEDNAVKY)}`
        + (jeCislo(i.trzba) ? ` za ${formatEur(i.trzba)}` : "")
        + (od ? ` od ${od}.` : "."),
    };
  },

  STALI_ZAKAZNICI: (i) => {
    if (!jeCislo(i.pocet) || i.pocet < 1 || !jeCislo(i.hranica)) return null;
    return {
      Icon: Users,
      ton: "primary",
      nadpis: "Stáli zákazníci",
      hodnota: `${i.pocet} ${sklon(i.pocet, ZAKAZNICI)}`,
      popis: `${i.pocet === 1 ? "Má" : "Majú"} aspoň ${i.hranica} objednávok`
        + (jeCislo(i.podielObjednavok)
          ? ` a ${i.pocet === 1 ? "tvorí" : "tvoria"} ${formatPercento(i.podielObjednavok)} všetkých objednávok`
          : "")
        + (jeCislo(i.zakaznikov) ? ` (z ${i.zakaznikov} zákazníkov).` : "."),
    };
  },

  KOMBINACIA: (i) => {
    if (!jeText(i.zaklad) || !jeText(i.doplnok) || !jeCislo(i.spolu)) return null;
    return {
      Icon: Utensils,
      ton: "primary",
      nadpis: "Najčastejšia kombinácia",
      hodnota: `${i.zaklad} + ${i.doplnok}`,
      // Názvy sú už v hodnote -- dlhé jedlá by sa inak v popise opakovali.
      popis: `Objednané spolu ${i.spolu}×`
        + (jeCislo(i.podiel) ? ` – v ${formatPercento(i.podiel)} objednávok prvého z nich.` : "."),
    };
  },

  CAS_OBJEDNAVOK: (i) => {
    if (!jeText(i.median) || !/^\d{2}:\d{2}$/.test(i.median) || !jeCislo(i.spickaHodina)) return null;
    const cas = formatCas(i.median);
    const od = i.spickaHodina;
    return {
      Icon: Clock,
      ton: "info",
      nadpis: "Kedy prichádzajú objednávky",
      hodnota: `do ${cas}`,
      popis: `Polovica objednávok na daný deň príde do ${cas}.`
        + ` Najviac ich chodí medzi ${od}:00 a ${(od + 1) % 24}:00`
        + (jeCislo(i.spickaPodiel) ? ` (${formatPercento(i.spickaPodiel)}).` : ".")
        + (jeCislo(i.vopredPodiel) && Math.round(i.vopredPodiel * 100) > 0
          ? ` ${formatPercento(i.vopredPodiel)} objednávok príde už deň vopred či skôr.`
          : ""),
    };
  },

  VYPREDANE: (i) => {
    if (!jeText(i.nazov) || !jeCislo(i.vypredane) || !jeCislo(i.ponuk)) return null;
    return {
      Icon: Flame,
      ton: "warning",
      nadpis: "Vypredáva sa",
      hodnota: i.nazov,
      popis: `Dosiahlo plánovaný limit ${i.vypredane}× z ${i.ponuk} dní v ponuke – oplatí sa ho navariť viac.`,
    };
  },

  SLABE_JEDLO: (i) => {
    if (!jeText(i.nazov) || !jeCislo(i.priemerKs) || !jeCislo(i.priemerOstatnych)) return null;
    return {
      Icon: TrendingDown,
      ton: "muted",
      nadpis: "Najslabšie jedlo",
      hodnota: i.nazov,
      popis: `V priemere ${formatKusy(i.priemerKs)}${NBSP}ks za deň v ponuke, ostatné hlavné jedlá `
        + `${formatKusy(i.priemerOstatnych)}${NBSP}ks. Zvážte úpravu receptu, ceny alebo vyradenie.`,
    };
  },
};

/** Zaujímavosť z API -> { kluc, Icon, ton, nadpis, hodnota, popis } alebo null. */
export function formatInsight(insight) {
  if (!insight || typeof insight !== "object") return null;
  const format = FORMATY[insight.typ];
  if (!format) return null;
  const vysledok = format(insight);
  return vysledok ? { kluc: insight.typ, ...vysledok } : null;
}

/** Podnadpis panelu: od kedy sa počíta. */
export function formatObdobie(obdobie) {
  const od = formatDatum(obdobie?.od);
  return od ? `Za celú históriu od ${od}` : "Za celú históriu";
}
